import type { JSX } from "solid-js";
import { onCleanup, onMount } from "solid-js";

export function GovernSheet(props: {
  title: string;
  returnFocus: HTMLElement | null;
  children: JSX.Element;
  onClose: () => void;
}) {
  let panel: HTMLElement | undefined;

  onMount(() =>
    queueMicrotask(() =>
      panel
        ?.querySelector<HTMLElement>("input, textarea, select, button")
        ?.focus(),
    ),
  );
  onCleanup(() => queueMicrotask(() => props.returnFocus?.focus()));

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      props.onClose();
      return;
    }
    if (event.key !== "Tab" || !panel) return;
    const controls = focusableControls(panel);
    const first = controls[0];
    const last = controls.at(-1);
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div class="govern-sheet-layer">
      <button
        type="button"
        class="govern-sheet-scrim"
        aria-label={`Close ${props.title}`}
        onClick={props.onClose}
      />
      <aside
        ref={panel}
        class="govern-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="govern-sheet-title"
        onKeyDown={onKeyDown}
      >
        <header>
          <div>
            <span class="signal-mini-kicker">Preview edit</span>
            <h2 id="govern-sheet-title">{props.title}</h2>
          </div>
          <button
            type="button"
            class="govern-sheet-close"
            aria-label={`Close ${props.title}`}
            onClick={props.onClose}
          >
            x
          </button>
        </header>
        <div class="govern-sheet-body">
          <p class="govern-sheet-intro">
            This change is local to the current Preview session and is not
            saved.
          </p>
          {props.children}
        </div>
      </aside>
    </div>
  );
}

function focusableControls(root: HTMLElement): HTMLElement[] {
  return [
    ...root.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ];
}
